import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { getPosthog } from './posthog-client';

type CookieScriptCategory = 'strict' | 'performance' | 'targeting' | 'functionality' | 'unclassified';

interface CookieScriptConsentState {
  action: 'accept' | 'reject';
  categories: CookieScriptCategory[];
}

interface RawCookieState {
  action?: unknown;
  categories?: unknown;
}

interface CookieScriptAcceptEvent extends Event {
  detail?: {
    categories?: CookieScriptCategory[];
  };
}

declare global {
  interface Window {
    CookieScript?: {
      instance?: {
        currentState?: () => CookieScriptConsentState;
      };
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class PostHogConsentService {
  private readonly cookieConsentName = 'CookieScriptConsent';
  private isListening = false;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  init(): void {
    if (!isPlatformBrowser(this.platformId) || this.isListening) {
      return;
    }

    this.isListening = true;

    window.addEventListener('CookieScriptAcceptAll', () => this.optIn());
    window.addEventListener('CookieScriptAccept', (event: CookieScriptAcceptEvent) => {
      this.applyCategories(event.detail?.categories);
    });
    window.addEventListener('CookieScriptReject', () => this.optOut());
    window.addEventListener('CookieScriptLoaded', () => this.syncCurrentState());

    this.syncConsent();
  }

  syncConsent(): void {
    this.syncCurrentState();
    this.syncStoredConsent();
  }

  private syncCurrentState(): void {
    if (!this.hasPendingConsent()) {
      return;
    }

    const state = window.CookieScript?.instance?.currentState?.();

    if (!state) {
      return;
    }

    if (state.action === 'reject') {
      this.optOut();
      return;
    }

    this.applyCategories(state.categories);
  }

  private syncStoredConsent(): void {
    if (!isPlatformBrowser(this.platformId) || !this.hasPendingConsent()) {
      return;
    }

    const state = this.parseCookie(this.getCookieValue(this.cookieConsentName));

    if (!state) {
      return;
    }

    if (state.action === 'reject') {
      this.optOut();
      return;
    }

    this.applyCategories(state.categories);
  }

  private applyCategories(categories: CookieScriptCategory[] = []): void {
    if (categories.includes('performance')) {
      this.optIn();
      return;
    }

    this.optOut();
  }

  private optIn(): void {
    getPosthog()?.opt_in_capturing();
  }

  private optOut(): void {
    getPosthog()?.opt_out_capturing();
  }

  private hasPendingConsent(): boolean {
    const posthog = getPosthog();
    return Boolean(posthog?.get_explicit_consent_status() === 'pending');
  }

  private getCookieValue(name: string): string | null {
    const cookie = document.cookie
      .split('; ')
      .find((item) => item.startsWith(`${name}=`));
    return cookie ? cookie.slice(name.length + 1) : null;
  }

  private parseCookie(value: string | null): CookieScriptConsentState | null {
    if (!value) {
      return null;
    }

    const rawState = this.parseCookieJson(value);

    if (!rawState || (rawState.action !== 'accept' && rawState.action !== 'reject')) {
      return null;
    }

    const categories = this.parseCategories(rawState.categories);

    if (!categories) {
      return null;
    }

    return {
      action: rawState.action,
      categories,
    };
  }

  private parseCookieJson(value: string): RawCookieState | null {
    try {
      return JSON.parse(decodeURIComponent(value));
    } catch (e) {
      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    }
  }

  private parseCategories(value: unknown): CookieScriptCategory[] | null {
    if (Array.isArray(value)) {
      return this.filterCookieCategories(value);
    }

    if (typeof value === 'string') {
      const parsedCategories = this.parseCategoriesString(value);
      return parsedCategories ? this.filterCookieCategories(parsedCategories) : null;
    }

    return null;
  }

  private parseCategoriesString(value: string): unknown[] | null {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : null;
    } catch (e) {
      return value.split(/[,\s]+/).filter(Boolean);
    }
  }

  private filterCookieCategories(categories: unknown[]): CookieScriptCategory[] {
    const knownCategories: CookieScriptCategory[] = ['strict', 'performance', 'targeting', 'functionality', 'unclassified'];
    return categories.filter((category): category is CookieScriptCategory =>
      typeof category === 'string' && knownCategories.includes(category as CookieScriptCategory));
  }
}
